import { createSelector, Selector } from '@reduxjs/toolkit';
import * as R from 'ramda';

import { ReduxApplicationState } from 'src/redux/combinedReducer';
import type { Filter, FilterOptionValue, SelectedFilterOption } from 'src/types';
import { getTitleFromFilterOptionUid } from './selectorHelpers';
import { selectFilters, selectSelectedFilterOptions } from './selectors';

export type FilterOptionSelectionTypes = 'SELECT' | 'DESELECT';

const selectFilterOptionPayload = (_state: ReduxApplicationState, payload: SelectedFilterOption) =>
  payload;

export interface FilterOptionSelection {
  type: FilterOptionSelectionTypes;
  filterTitle: string;
  filterOptionTitle: string;
  filterUid: string;
  filterOptionValue: FilterOptionValue;
}

export const getFilterOptionSelection = (
  newFilterOption: SelectedFilterOption,
  filters: Filter[],
  selectedFilterOptions: SelectedFilterOption[],
): FilterOptionSelection => {
  const { filterOptionValue, filterUid, filterOptionUid } = newFilterOption;

  const filter = filters.find((filter) => filter.uid === filterUid);

  if (Array.isArray(filterOptionValue)) {
    const { filterOptionValue: values } = selectedFilterOptions.find(
      (selectedFilter) => selectedFilter.filterUid === filterUid,
    ) ?? { filterOptionValue: [] };

    const selectedOption = R.difference(filterOptionValue, values as string[]).join('');
    const deslectedOption = R.difference(values as string[], filterOptionValue).join('');

    const valueToMatch = selectedOption || deslectedOption;

    const filterOptionTitle = filter!.options.find(({ value }) => value === valueToMatch)!.title;

    const type = selectedOption ? 'SELECT' : 'DESELECT';

    return {
      type,
      filterTitle: filter!.title,
      filterOptionTitle,
      filterUid,
      filterOptionValue: valueToMatch,
    };
  } else {
    let filterOptionTitle;

    if (filter!.type == 'range' && filterOptionUid) {
      filterOptionTitle = `${getTitleFromFilterOptionUid(filterOptionUid)}:${filterOptionValue}`;
    } else {
      filterOptionTitle = filter!.options.find(({ value }) => value === filterOptionValue)!.title;
    }

    return {
      type: 'SELECT',
      filterTitle: filter!.title,
      filterOptionTitle,
      filterUid,
      filterOptionValue,
    };
  }
};

export const selectFilterOptionSelection: Selector<ReduxApplicationState, FilterOptionSelection> =
  createSelector(
    [selectFilters, selectSelectedFilterOptions, selectFilterOptionPayload],
    (filters, selectedFilterOptions, payload) =>
      getFilterOptionSelection(payload, filters, selectedFilterOptions),
  );
